import React, { useEffect, useState, useRef } from 'react';
import { Video, Loader2, Save, Trash2, Check, Upload, Image as ImageIcon, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { adminApi, BACKEND_URL } from '../lib/api';
import ImageUpload from './ImageUpload';

const SiteSettings = () => {
  const [settings, setSettings] = useState({
    hero_video_url: '', hero_video_poster: '', hero_video_enabled: false, hero_video_title: '',
    experience_image: '', gallery_images: [], hero_slides: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminApi.getSettings ? adminApi.getSettings().then((s) => {
      setSettings({
        hero_video_url: s.hero_video_url || '',
        hero_video_poster: s.hero_video_poster || '',
        hero_video_enabled: s.hero_video_enabled || false,
        hero_video_title: s.hero_video_title || '',
        experience_image: s.experience_image || '',
        gallery_images: Array.isArray(s.gallery_images) ? s.gallery_images : [],
        hero_slides: Array.isArray(s.hero_slides) ? s.hero_slides : [],
      });
      setLoading(false);
    }) : setLoading(false);
  }, []);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      await adminApi.updateSettings({
        hero_video_url: settings.hero_video_url || null,
        hero_video_poster: settings.hero_video_poster || null,
        hero_video_enabled: settings.hero_video_enabled,
        hero_video_title: settings.hero_video_title || null,
        experience_image: settings.experience_image || null,
        gallery_images: settings.gallery_images,
        hero_slides: settings.hero_slides,
      });
      setMessage('Paramètres enregistrés');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage('Erreur : ' + (e?.response?.data?.detail || 'inconnue'));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer la vidéo du hero ?')) return;
    setSaving(true);
    try {
      await adminApi.updateSettings({ hero_video_url: '', hero_video_enabled: false });
      setSettings({ hero_video_url: '', hero_video_poster: '', hero_video_enabled: false, hero_video_title: settings.hero_video_title });
      setMessage('Vidéo supprimée · le carrousel d\'images est réactivé');
      setTimeout(() => setMessage(''), 3000);
    } finally { setSaving(false); }
  };

  const fileRef = useRef(null);
  const previewRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadStats, setUploadStats] = useState(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setMessage('Erreur : le fichier doit être une vidéo (MP4, WebM, MOV)');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setMessage('Erreur : vidéo trop lourde (max 25 Mo)');
      return;
    }
    setUploading(true); setUploadPct(0); setMessage(''); setUploadStats(null);
    try {
      const res = await adminApi.uploadHeroVideo(file, setUploadPct);
      const fullUrl = `${BACKEND_URL}${res.url}`;
      await adminApi.updateSettings({
        hero_video_url: fullUrl,
        hero_video_enabled: true,
        hero_video_title: settings.hero_video_title || 'Promos vacances · Bénin'
      });
      setSettings({ ...settings, hero_video_url: fullUrl, hero_video_enabled: true });
      setUploadStats({
        compressed: res.compressed,
        original_mb: (res.original_size / (1024*1024)).toFixed(1),
        final_mb: (res.size / (1024*1024)).toFixed(1),
        savings_pct: res.original_size > res.size
          ? Math.round((1 - res.size / res.original_size) * 100)
          : 0
      });
      setMessage('Vidéo téléversée et prête · aperçu ci-dessous');
      setTimeout(() => setMessage(''), 5000);
      // Auto-play preview and scroll to it
      setTimeout(() => {
        if (previewRef.current) {
          previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const v = previewRef.current.querySelector('video');
          if (v) { v.currentTime = 0; v.play().catch(() => {}); }
        }
      }, 300);
    } catch (err) {
      setMessage('Erreur upload : ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false); setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Paramètres du site</h3>
        <p className="text-sm text-slate-500">Gérez la vidéo affichée en fond du hero de la page d'accueil</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Video className="w-5 h-5 text-[#E89534]" />
          <h4 className="font-bold text-slate-900">Vidéo du hero</h4>
        </div>

        {/* Direct upload */}
        <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-dashed border-[#F4A340]/40 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[#F4A340]/20 flex items-center justify-center flex-shrink-0">
              <Upload className="w-5 h-5 text-[#E89534]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900">Téléverser une nouvelle vidéo</div>
              <p className="text-xs text-slate-500 mt-0.5">MP4/WebM/MOV · Max 25 Mo · Format vertical recommandé (9:16)</p>
              <input ref={fileRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" data-testid="settings-video-file-input" />
              <div className="flex flex-wrap gap-2 mt-3 items-center">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="settings-upload-video-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-300 text-white text-sm font-semibold rounded-full transition"
                >
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> {uploadPct}%</> : <><Upload className="w-4 h-4" /> Choisir la vidéo</>}
                </button>
                {uploading && (
                  <div className="flex-1 min-w-[120px] h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-[#F4A340] transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Ou coller une URL de vidéo (MP4)</label>
          <input
            placeholder="https://.../video.mp4"
            value={settings.hero_video_url}
            onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">La vidéo se lira automatiquement en boucle, muette, sur toute la largeur du hero</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">URL de l'image poster (optionnel)</label>
          <input
            placeholder="https://.../poster.jpg"
            value={settings.hero_video_poster}
            onChange={(e) => setSettings({ ...settings, hero_video_poster: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">Affichée pendant le chargement de la vidéo</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Titre / Légende sous la vidéo</label>
          <input
            data-testid="settings-hero-video-title-input"
            placeholder="Ex : Promos vacances · Bénin"
            maxLength={60}
            value={settings.hero_video_title}
            onChange={(e) => setSettings({ ...settings, hero_video_title: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">Affiché en permanence sous la vidéo (promo, nouveauté, événement...). Max 60 caractères.</p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-xl">
          <input
            type="checkbox"
            checked={settings.hero_video_enabled}
            onChange={(e) => setSettings({ ...settings, hero_video_enabled: e.target.checked })}
            className="w-5 h-5 accent-[#F4A340]"
          />
          <div>
            <div className="font-semibold text-sm text-slate-900">Activer la vidéo</div>
            <div className="text-xs text-slate-500">Si désactivé, le carrousel d'images sera affiché à la place</div>
          </div>
        </label>

        {settings.hero_video_url && (
          <div ref={previewRef} className="pt-2" data-testid="settings-video-preview">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-bold text-slate-900">Aperçu (rendu réel sur le site)</h5>
              {uploadStats && (
                <div className="text-xs font-semibold" data-testid="settings-compression-stats">
                  {uploadStats.compressed && uploadStats.savings_pct > 0 ? (
                    <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      Compressée · {uploadStats.original_mb} Mo → {uploadStats.final_mb} Mo (−{uploadStats.savings_pct}%)
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                      {uploadStats.final_mb} Mo · légère, non compressée
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-slate-100 to-slate-50 rounded-xl">
              <div className="w-40 flex-shrink-0">
                <div className="relative aspect-[9/16] bg-slate-900 rounded-2xl overflow-hidden border-4 border-white shadow-lg">
                  <video
                    key={settings.hero_video_url}
                    src={settings.hero_video_url}
                    poster={settings.hero_video_poster || undefined}
                    autoPlay muted loop playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-widest">
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> Live
                  </div>
                  {settings.hero_video_title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent pt-6 pb-2 px-2">
                      <div className="text-white text-[10px] font-bold text-center leading-tight drop-shadow-lg">
                        {settings.hero_video_title}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 text-xs text-slate-500 space-y-1 pt-1">
                <p>Voilà exactement ce que verront vos visiteurs sur la page d'accueil.</p>
                <p>La vidéo se lit en boucle, muette par défaut, avec un bouton son.</p>
                <p>Le titre apparaît en overlay sur la vignette.</p>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" /> {message}
          </div>
        )}

        {/* ============ Section Expérience lagunaire ============ */}
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-orange-500" />
            Photo section « Expérience lagunaire »
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">URL ou upload</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={settings.experience_image}
                  onChange={(e) => setSettings({ ...settings, experience_image: e.target.value })}
                  placeholder="https://... ou uploader"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm"
                  data-testid="settings-experience-image"
                />
                <ImageUpload
                  onUploaded={(url) => setSettings({ ...settings, experience_image: url })}
                  testId="settings-experience-upload"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Colle une URL OU uploade depuis ton ordinateur (max 8 Mo, JPG/PNG/WebP).
              </p>
            </div>
            {settings.experience_image && (
              <div className="rounded-xl overflow-hidden aspect-[4/5] max-h-64 border border-slate-200">
                <img src={settings.experience_image} alt="Aperçu" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>

        {/* ============ Section Carrousel Hero ============ */}
        <div className="border-t border-slate-200 pt-6 mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-orange-500" />
              Carrousel de la page d'accueil
              <span className="text-xs font-normal text-slate-500">
                ({settings.hero_slides.length}/6 slides)
              </span>
            </h3>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, hero_slides: [...settings.hero_slides, { image: '', title: '', titleAccent: '', subtitle: '' }] })}
              disabled={settings.hero_slides.length >= 6}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 hover:bg-orange-200 disabled:bg-slate-100 disabled:text-slate-400 text-orange-700 text-sm font-semibold"
              data-testid="settings-slides-add"
            >
              <Plus className="w-4 h-4" /> Ajouter un slide
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Diaporama qui défile en fond de la page d'accueil. Recommandé : 4 slides, format
            paysage 16:9 haute résolution.
          </p>
          <div className="space-y-3">
            {settings.hero_slides.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200" data-testid={`settings-slide-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase text-slate-500">Slide {i + 1}</span>
                  <div className="flex-1"></div>
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => {
                      const next = [...settings.hero_slides];
                      [next[i - 1], next[i]] = [next[i], next[i - 1]];
                      setSettings({ ...settings, hero_slides: next });
                    }}
                    className="p-1 rounded text-slate-500 hover:bg-white disabled:opacity-30"
                  ><ChevronUp className="w-4 h-4" /></button>
                  <button
                    type="button"
                    disabled={i === settings.hero_slides.length - 1}
                    onClick={() => {
                      const next = [...settings.hero_slides];
                      [next[i + 1], next[i]] = [next[i], next[i + 1]];
                      setSettings({ ...settings, hero_slides: next });
                    }}
                    className="p-1 rounded text-slate-500 hover:bg-white disabled:opacity-30"
                  ><ChevronDown className="w-4 h-4" /></button>
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, hero_slides: settings.hero_slides.filter((_, k) => k !== i) })}
                    className="p-1 rounded text-red-500 hover:bg-red-50"
                  ><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-3">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                    {s.image
                      ? <img src={s.image} alt="Aperçu" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-6 h-6" /></div>}
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={s.image}
                        onChange={(e) => {
                          const next = [...settings.hero_slides];
                          next[i] = { ...next[i], image: e.target.value };
                          setSettings({ ...settings, hero_slides: next });
                        }}
                        placeholder="URL de l'image (ou uploader →)"
                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                        data-testid={`settings-slide-${i}-image`}
                      />
                      <ImageUpload
                        onUploaded={(url) => {
                          const next = [...settings.hero_slides];
                          next[i] = { ...next[i], image: url };
                          setSettings({ ...settings, hero_slides: next });
                        }}
                        size="sm"
                        testId={`settings-slide-${i}-upload`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={s.title}
                        onChange={(e) => {
                          const next = [...settings.hero_slides];
                          next[i] = { ...next[i], title: e.target.value };
                          setSettings({ ...settings, hero_slides: next });
                        }}
                        placeholder="Titre (ex: Le quai, juste avant)"
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                      />
                      <input
                        type="text"
                        value={s.titleAccent}
                        onChange={(e) => {
                          const next = [...settings.hero_slides];
                          next[i] = { ...next[i], titleAccent: e.target.value };
                          setSettings({ ...settings, hero_slides: next });
                        }}
                        placeholder="Mot en orange (ex: le départ)"
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={s.subtitle}
                      onChange={(e) => {
                        const next = [...settings.hero_slides];
                        next[i] = { ...next[i], subtitle: e.target.value };
                        setSettings({ ...settings, hero_slides: next });
                      }}
                      placeholder="Sous-titre (ex: Une aventure lagunaire...)"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
            {settings.hero_slides.length === 0 && (
              <p className="text-sm text-slate-500 italic text-center py-4">
                Aucun slide personnalisé · le carrousel par défaut sera affiché.
              </p>
            )}
          </div>
        </div>

        {/* ============ Section Galerie ============ */}
        <div className="border-t border-slate-200 pt-6 mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-orange-500" />
              Galerie du site
              <span className="text-xs font-normal text-slate-500">
                ({settings.gallery_images.length}/4 images)
              </span>
            </h3>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, gallery_images: [...settings.gallery_images, ''] })}
              disabled={settings.gallery_images.length >= 4}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 hover:bg-orange-200 disabled:bg-slate-100 disabled:text-slate-400 text-orange-700 text-sm font-semibold transition-colors"
              data-testid="settings-gallery-add"
            >
              <Plus className="w-4 h-4" /> Ajouter une image
            </button>
          </div>

          <p className="text-xs text-slate-500 mb-3">
            La galerie est limitée à <strong>4 images</strong> pour un affichage mosaïque
            équilibré (1 grande à gauche + 1 large en haut + 2 carrées en bas).
            <br />
            <span className="text-slate-400">
              L'image <strong>1</strong> doit être verticale ou carrée, l'image <strong>2</strong> panoramique, les <strong>3</strong> et <strong>4</strong> carrées.
            </span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {settings.gallery_images.map((url, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`settings-gallery-row-${i}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden">
                  {url ? (
                    <img src={url} alt={`Preview ${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <ImageIcon className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    const next = [...settings.gallery_images];
                    next[i] = e.target.value;
                    setSettings({ ...settings, gallery_images: next });
                  }}
                  placeholder="https://..."
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm"
                />
                <ImageUpload
                  onUploaded={(url) => {
                    const next = [...settings.gallery_images];
                    next[i] = url;
                    setSettings({ ...settings, gallery_images: next });
                  }}
                  size="sm"
                  testId={`settings-gallery-upload-${i}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = settings.gallery_images.filter((_, idx) => idx !== i);
                    setSettings({ ...settings, gallery_images: next });
                  }}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`settings-gallery-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {settings.gallery_images.length === 0 && (
              <p className="text-sm text-slate-500 italic col-span-full text-center py-4">
                Aucune image personnalisée · la galerie par défaut sera affichée.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          {settings.hero_video_url && (
            <button onClick={remove} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-full text-red-600 hover:bg-red-50 font-semibold">
              <Trash2 className="w-4 h-4" /> Supprimer la vidéo
            </button>
          )}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full" data-testid="settings-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;
